import { api } from '../api.ts';
import { grantAccess, type DocumentMeta } from '../documents.ts';
import { alsVertrauenspersonKarte, vertrauenspersonenKarte } from '../vertrauensperson-ui.ts';
import { gemeinsameSafesKarten, schluesselKarte } from './schluessel.ts';
import { clear, el, formatBytes, formatDate, formatRemaining, hinweis, karte } from '../ui.ts';
import {
  AKTION_TEXT,
  fehlertext,
  kopfzeile,
  protokollTabelle,
  root,
  sorgeFuerEntschluesselte,
  sorgeFuerMetadaten,
  state,
  ticket,
  type LogRow,
} from './gemeinsam.ts';

/**
 * Ansicht „Freigaben" (nur Kunden): Zugriff für den Ansprechpartner,
 * Vertrauenspersonen für den Notfall und das Zugriffsprotokoll.
 *
 * Braucht nur die Metadaten – die gewrappten Schlüssel der Dokumente reichen
 * zum Weiterreichen, entschlüsselt werden muss dafür nichts.
 */

export async function renderFreigaben(): Promise<void> {
  clear(root);
  root.append(kopfzeile('freigaben'), el('p', { class: 'loading' }, 'Wird geladen …'));

  try {
    await sorgeFuerMetadaten();
  } catch (error) {
    clear(root);
    root.append(kopfzeile('freigaben'), hinweis(fehlertext(error), 'warnung'));
    return;
  }

  clear(root);
  root.append(
    kopfzeile('freigaben'),
    // Familien-Safes zuerst: Das ist die Freigabe, die im Alltag vorkommt.
    ...(state.me?.personId ? [schluesselKarte(), ...gemeinsameSafesKarten()] : []),
    // Ein Gast hat keine eigenen Unterlagen – Buddy-Freigabe und Vertrauenspersonen entfallen.
    ...(state.me?.gast ? [] : [freigabeKarte(), vertrauenspersonenKarte()]),
    alsVertrauenspersonKarte(),
    protokollKarte(),
  );
}

// --- Zugriff für den Ansprechpartner ---------------------------------------

/** Längste Freigabe in Tagen – deckungsgleich mit MAX_GRANT_DAYS im Backend. */
const MAX_GRANT_DAYS = 365;

/** Ein Datum als `JJJJ-MM-TT` in Ortszeit, wie es ein `<input type="date">` erwartet. */
function alsTagesangabe(datum: Date): string {
  const zweistellig = (wert: number) => String(wert).padStart(2, '0');
  return `${datum.getFullYear()}-${zweistellig(datum.getMonth() + 1)}-${zweistellig(datum.getDate())}`;
}

/**
 * Tagesende des gewählten Tages, in der Zeitzone des Kunden.
 *
 * Bewusst von Hand zerlegt statt `new Date('2026-09-18')`: Ein reiner
 * Datums-String wird von JavaScript als UTC-Mitternacht gelesen. In
 * Deutschland wäre die Freigabe damit schon um 01:00 bzw. 02:00 Uhr des
 * gewählten Tages vorbei — also bevor der Tag richtig angefangen hat.
 */
function tagesende(tagesangabe: string): Date {
  const [jahr, monat, tag] = tagesangabe.split('-').map(Number);
  return new Date(jahr!, monat! - 1, tag!, 23, 59, 59, 999);
}

function freigabeKarte(): HTMLElement {
  const status = el('div', { id: 'freigabe-status' });
  const liste = el('div', { id: 'freigabe-liste' });
  void ladeFreigaben(liste);

  // Vorbelegt ist der heutige Tag: die kürzeste Freigabe, die sich mit der
  // Tagesende-Regel überhaupt erteilen lässt. Wer länger braucht, schiebt das
  // Datum bewusst nach vorn — bis maximal ein Jahr.
  const heute = new Date();
  const letzterTag = new Date(heute.getTime());
  letzterTag.setDate(letzterTag.getDate() + MAX_GRANT_DAYS);

  const auswahlBereich = el('div', { id: 'freigabe-auswahl', hidden: true });

  return karte(
    el('h2', {}, 'Zugriff für Ihren Ansprechpartner'),
    // Steht ein Ticket im Link, ist der Anlass schon benannt. Genau darum geht
    // es in Konzept 6.6: „für Ticket T-1042: Rentenantrag“ statt „Ihr Buddy
    // möchte Zugriff“ — der Kunde weiß, worauf er sich einlässt und wofür.
    ticket
      ? hinweis(
          `Angefragt für Ticket ${ticket.nummer}` +
            (ticket.betreff ? `: ${ticket.betreff}` : '') +
            '. Dieser Anlass wird an der Freigabe vermerkt und steht später in ' +
            'Ihrem Protokoll.',
        )
      : null,
    el(
      'p',
      {},
      'Solange eine Freigabe läuft, kann Ihr Ansprechpartner die freigegebenen Dokumente ' +
        'sehen und dabei KI-Unterstützung nutzen, um Ihnen schneller zu helfen. ' +
        'Danach nicht mehr.',
    ),
    hinweis(
      'Was Ihr Ansprechpartner während der Freigabe gesehen hat, weiß er danach ' +
        'weiterhin – so ist das bei jeder Form von Weitergabe. Die Freigabe ' +
        'verhindert neuen Zugriff nach Ablauf, nicht die Erinnerung.',
    ),
    el('label', {}, 'Umfang'),
    el(
      'div',
      { style: 'display:flex;gap:.6rem;align-items:center;margin:.4rem 0' },
      el('input', {
        type: 'radio',
        id: 'umfang-alle',
        name: 'freigabe-umfang',
        value: 'all',
        checked: true,
        style: 'width:auto;margin:0',
        onchange: () => umfangUmschalten('all', auswahlBereich),
      }),
      el('label', { for: 'umfang-alle', style: 'margin:0;font-weight:400' }, 'Gesamter Akten-Safe'),
    ),
    el(
      'div',
      { style: 'display:flex;gap:.6rem;align-items:center;margin:.4rem 0' },
      el('input', {
        type: 'radio',
        id: 'umfang-auswahl',
        name: 'freigabe-umfang',
        value: 'documents',
        style: 'width:auto;margin:0',
        onchange: () => umfangUmschalten('documents', auswahlBereich),
      }),
      el(
        'label',
        { for: 'umfang-auswahl', style: 'margin:0;font-weight:400' },
        'Bestimmte Dokumente auswählen',
      ),
    ),
    auswahlBereich,
    el('label', { for: 'freigabe-bis' }, 'Freigabe gilt bis Ende des Tages'),
    el('input', {
      type: 'date',
      id: 'freigabe-bis',
      value: alsTagesangabe(heute),
      min: alsTagesangabe(heute),
      max: alsTagesangabe(letzterTag),
    }),
    el('label', { for: 'freigabe-grund' }, 'Anlass (freiwillig)'),
    el('input', {
      type: 'text',
      id: 'freigabe-grund',
      placeholder: 'z. B. Hilfe bei Rechnung',
      value: ticket?.betreff ?? '',
    }),
    status,
    el(
      'div',
      { class: 'knopfreihe' },
      el(
        'button',
        { id: 'freigeben', onclick: () => void freigeben(status) },
        'Freigabe erteilen',
      ),
    ),
    el('h3', {}, 'Bestehende Freigaben'),
    liste,
  );
}

/**
 * Wechselt zwischen „ganzer Akten-Safe" und „Auswahl". Die Dokumentnamen
 * sind verschlüsselt (`filename_encrypted`) — für eine sinnvolle Auswahl
 * müssen sie entschlüsselt sein, was hier bewusst NICHT beim Laden der
 * Ansicht passiert (die kommt sonst mit den reinen Metadaten aus), sondern
 * erst, wenn der Kunde tatsächlich einzelne Dokumente auswählen will.
 */
function umfangUmschalten(umfang: 'all' | 'documents', auswahlBereich: HTMLElement): void {
  if (umfang === 'all') {
    auswahlBereich.hidden = true;
    return;
  }
  auswahlBereich.hidden = false;
  if (auswahlBereich.childElementCount > 0) return;

  clear(auswahlBereich);
  auswahlBereich.append(el('p', { class: 'leise' }, 'Dokumente werden entschlüsselt …'));
  void sorgeFuerEntschluesselte()
    .then(() => {
      clear(auswahlBereich);
      if (state.opened.length === 0) {
        auswahlBereich.append(hinweis('Es sind noch keine Dokumente vorhanden.', 'warnung'));
        return;
      }
      auswahlBereich.append(
        el(
          'ul',
          { class: 'dokumentliste', id: 'freigabe-dokumentliste' },
          ...state.opened.map((dokument) => {
            const id = `freigabe-dok-${dokument.id}`;
            return el(
              'li',
              { style: 'display:flex;gap:.6rem;align-items:center' },
              el('input', {
                type: 'checkbox',
                id,
                class: 'freigabe-dokument-wahl',
                value: dokument.id,
                style: 'width:auto;margin:0',
              }),
              el(
                'label',
                { for: id, style: 'margin:0;font-weight:400' },
                `${dokument.filename} (${formatBytes(dokument.meta.size_bytes)})`,
              ),
            );
          }),
        ),
      );
    })
    .catch((error: unknown) => {
      clear(auswahlBereich);
      auswahlBereich.append(hinweis(fehlertext(error), 'warnung'));
    });
}

function ausgewaehlteDokumente(): DocumentMeta[] {
  const gewaehlteIds = new Set(
    [...root.querySelectorAll<HTMLInputElement>('.freigabe-dokument-wahl')]
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.value),
  );
  return state.documents.filter((meta) => gewaehlteIds.has(meta.id));
}

async function freigeben(status: HTMLElement): Promise<void> {
  const bis = root.querySelector<HTMLInputElement>('#freigabe-bis')!.value;
  const grund = root.querySelector<HTMLInputElement>('#freigabe-grund')!.value.trim();
  const button = root.querySelector<HTMLButtonElement>('#freigeben')!;
  const umfang =
    root.querySelector<HTMLInputElement>('input[name="freigabe-umfang"]:checked')!.value === 'all'
      ? 'all'
      : 'documents';

  clear(status);
  if (!bis) {
    status.append(hinweis('Bitte geben Sie an, bis wann die Freigabe gelten soll.', 'warnung'));
    return;
  }
  const gueltigBis = tagesende(bis);
  if (Number.isNaN(gueltigBis.getTime()) || gueltigBis <= new Date()) {
    status.append(hinweis('Bitte wählen Sie einen Tag, der nicht in der Vergangenheit liegt.', 'warnung'));
    return;
  }
  if (state.documents.length === 0) {
    status.append(hinweis('Es sind noch keine Dokumente vorhanden.', 'warnung'));
    return;
  }

  const dokumente = umfang === 'all' ? state.documents : ausgewaehlteDokumente();
  if (umfang === 'documents' && dokumente.length === 0) {
    status.append(hinweis('Bitte wählen Sie mindestens ein Dokument aus.', 'warnung'));
    return;
  }

  button.disabled = true;
  button.textContent = 'Schlüssel werden vorbereitet …';
  try {
    await grantAccess({
      documents: dokumente,
      scope: umfang,
      expiresAt: gueltigBis,
      reason: grund || undefined,
      ticketReference: ticket?.nummer,
      ticketSubject: ticket?.betreff ?? undefined,
    });
    clear(status);
    status.append(
      hinweis(
        umfang === 'all'
          ? `Freigabe erteilt. Ihr Ansprechpartner hat bis ${formatDate(gueltigBis)} Zugriff auf den gesamten Akten-Safe.`
          : `Freigabe erteilt. Ihr Ansprechpartner hat bis ${formatDate(gueltigBis)} Zugriff auf ${dokumente.length} Dokument(e).`,
        'erfolg',
      ),
    );
    await ladeFreigaben(root.querySelector<HTMLElement>('#freigabe-liste')!);
  } catch (error) {
    clear(status);
    status.append(hinweis(fehlertext(error), 'warnung'));
  } finally {
    button.disabled = false;
    button.textContent = 'Freigabe erteilen';
  }
}

interface GrantRow {
  id: string;
  buddy_name: string;
  granted_at: string;
  expires_at: string;
  revoked_at: string | null;
  document_count: number;
  is_active: boolean;
  reason: string | null;
  ticket_reference: string | null;
  ticket_subject: string | null;
}

/**
 * Wofür die Freigabe galt. Die Ticketnummer steht vorn: Danach fragt der Kunde
 * am Telefon, und danach findet der Buddy den Vorgang wieder. Der freiwillige
 * Freitext bleibt der Rückfall für Freigaben, die der Kunde von sich aus
 * erteilt hat.
 */
function anlasszeile(grant: GrantRow): HTMLElement | null {
  if (grant.ticket_reference) {
    return el(
      'div',
      { class: 'leise' },
      `Für Ticket ${grant.ticket_reference}` +
        (grant.ticket_subject ? `: ${grant.ticket_subject}` : ''),
    );
  }
  return grant.reason ? el('div', { class: 'leise' }, `Anlass: ${grant.reason}`) : null;
}

async function ladeFreigaben(container: HTMLElement): Promise<void> {
  clear(container);
  const { grants } = await api.get<{ grants: GrantRow[] }>('/api/grants');

  if (grants.length === 0) {
    container.append(el('p', { class: 'leise' }, 'Sie haben bisher keine Freigabe erteilt.'));
    return;
  }

  container.append(
    el(
      'ul',
      { class: 'dokumentliste' },
      ...grants.map((grant) =>
        el(
          'li',
          {},
          el(
            'div',
            {},
            el(
              'div',
              { class: 'dokumentname' },
              `${grant.document_count} Dokument(e) für ${grant.buddy_name}`,
            ),
            el(
              'div',
              { class: 'leise' },
              grant.is_active
                ? `Aktiv, ${formatRemaining(grant.expires_at)}`
                : grant.revoked_at
                  ? `Zurückgezogen am ${formatDate(grant.revoked_at)}`
                  : `Abgelaufen am ${formatDate(grant.expires_at)}`,
            ),
            anlasszeile(grant),
          ),
          grant.is_active
            ? el(
                'button',
                {
                  class: 'warnung',
                  onclick: () => void zurueckziehen(grant.id, container),
                },
                'Zugriff sofort beenden',
              )
            : null,
        ),
      ),
    ),
  );
}

async function zurueckziehen(grantId: string, container: HTMLElement): Promise<void> {
  await api.post(`/api/grants/${grantId}/revoke`);
  await ladeFreigaben(container);
}

// --- Zugriffsprotokoll -----------------------------------------------------

const PROTOKOLL_KURZ_ANZAHL = 5;

function protokollKarte(): HTMLElement {
  const container = el('div', { id: 'protokoll' });
  void ladeProtokoll(container);

  return karte(
    el('h2', {}, 'Wer hat wann zugegriffen'),
    el(
      'p',
      { class: 'leise' },
      'Hier sehen Sie jeden Zugriff auf Ihre Unterlagen. Was in den Dokumenten ' +
        'steht, sieht außer Ihnen niemand – auch in diesem Protokoll nicht.',
    ),
    container,
  );
}

async function ladeProtokoll(container: HTMLElement): Promise<void> {
  clear(container);
  const { entries } = await api.get<{ entries: LogRow[] }>(
    `/api/access-log?limit=${PROTOKOLL_KURZ_ANZAHL}`,
  );

  if (entries.length === 0) {
    container.append(el('p', { class: 'leise' }, 'Noch keine Einträge.'));
    return;
  }

  container.append(
    protokollTabelle(entries),
    el(
      'div',
      { class: 'knopfreihe' },
      el('a', { class: 'sekundaer knopf-link', href: '#/protokoll' }, 'Vollständige Historie anzeigen'),
    ),
  );
}

// --- Vollständige Zugriffshistorie ------------------------------------------

export async function renderProtokollVoll(): Promise<void> {
  clear(root);
  root.append(kopfzeile('freigaben'), el('p', { class: 'loading' }, 'Historie wird geladen …'));

  const { entries } = await api.get<{ entries: LogRow[] }>('/api/access-log?limit=500');

  const zurueckKnopf = el('a', { class: 'sekundaer knopf-link', href: '#/freigaben' }, 'Zurück');

  if (entries.length === 0) {
    clear(root);
    root.append(
      kopfzeile('freigaben'),
      karte(
        el('h2', {}, 'Vollständige Zugriffshistorie'),
        el('p', { class: 'leise' }, 'Noch keine Einträge.'),
        el('div', { class: 'knopfreihe' }, zurueckKnopf),
      ),
    );
    return;
  }

  const ergebnis = el('div', { id: 'protokoll-ergebnis' });
  const suche = el('input', {
    type: 'search',
    id: 'protokoll-suche',
    placeholder: 'Nach Person oder Aktion suchen …',
  }) as HTMLInputElement;
  const aktionsFilter = el(
    'select',
    { id: 'protokoll-aktion' },
    el('option', { value: '' }, 'Alle Aktionen'),
    ...Object.entries(AKTION_TEXT).map(([wert, text]) => el('option', { value: wert }, text)),
  ) as HTMLSelectElement;

  const aktualisieren = (): void => {
    const suchtext = suche.value.trim().toLowerCase();
    const aktion = aktionsFilter.value;
    const gefiltert = entries.filter((entry) => {
      if (aktion && entry.action !== aktion) return false;
      if (!suchtext) return true;
      const wer =
        entry.actor_role === 'customer' ? 'sie selbst' : (entry.actor_name ?? 'unbekannt');
      const was = AKTION_TEXT[entry.action] ?? entry.action;
      return wer.toLowerCase().includes(suchtext) || was.toLowerCase().includes(suchtext);
    });

    clear(ergebnis);
    ergebnis.append(
      gefiltert.length === 0
        ? el('p', { class: 'leise' }, 'Keine passenden Einträge.')
        : protokollTabelle(gefiltert),
    );
  };

  suche.addEventListener('input', aktualisieren);
  aktionsFilter.addEventListener('change', aktualisieren);

  clear(root);
  root.append(
    kopfzeile('freigaben'),
    karte(
      el('h2', {}, 'Vollständige Zugriffshistorie'),
      el(
        'p',
        { class: 'leise' },
        'Alle Zugriffe auf Ihre Unterlagen. Was in den Dokumenten steht, sieht ' +
          'außer Ihnen niemand – auch hier nicht.',
      ),
      el('div', { class: 'knopfreihe' }, suche, aktionsFilter),
      ergebnis,
      el('div', { class: 'knopfreihe' }, zurueckKnopf),
    ),
  );

  aktualisieren();
}
